import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, GraduationCap, Building2, FlaskConical } from "lucide-react";

// ── Animation helper ───────────────────────────────────────────────
const fadeUp = (delay = 0) => ({
  initial:    { opacity: 0, y: 28 },
  whileInView:{ opacity: 1, y: 0  },
  viewport:   { once: true, amount: 0.2 },
  transition: { duration: 0.55, ease: "easeOut", delay },
});

const fadeLeft = (delay = 0) => ({
  initial:    { opacity: 0, x: -32 },
  whileInView:{ opacity: 1, x: 0   },
  viewport:   { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: "easeOut", delay },
});

const fadeRight = (delay = 0) => ({
  initial:    { opacity: 0, x: 32 },
  whileInView:{ opacity: 1, x: 0  },
  viewport:   { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: "easeOut", delay },
});

// ── Data ──────────────────────────────────────────────────────────
const CONF_HIGHLIGHTS = [
  "Integration of eco-friendly materials and sustainable construction methods",
  "Green construction practices and renewable materials research",
  "Resilience, efficiency and durability in infrastructure development",
  "Interdisciplinary collaboration across engineering disciplines",
  "Cutting-edge advancements reducing environmental impact",
  "Balance between human development and ecological preservation",
];

const REVA_STATS = [
  { value: "43",  unit: "Acres",     label: "Green Campus"       },
  { value: "38",  unit: "UG",        label: "Programmes"         },
  { value: "31",  unit: "PG",        label: "Programmes"         },
  { value: "20",  unit: "Ph.D",      label: "Programmes"         },
];

const SCHOOL_PROGRAMS = [
  {
    icon: GraduationCap,
    title: "B.Tech",
    desc: "Civil Engineering",
  },
  {
    icon: FlaskConical,
    title: "M.Tech",
    desc: "Computer Aided Structural Engineering",
  },
  {
    icon: FlaskConical,
    title: "M.Tech",
    desc: "Transportation Engineering & Management",
  },
  {
    icon: FlaskConical,
    title: "M.Tech",
    desc: "Construction Technology & Management",
  },
  {
    icon: Building2,
    title: "Ph.D",
    desc: "Research Programme leading to Doctoral Degree",
  },
];

// ─────────────────────────────────────────────────────────────────
export default function AboutSection() {
  return (
    <section className="w-full bg-white">

      {/* ════════════════════════════════════════
          BLOCK 1 — About the Conference
      ════════════════════════════════════════ */}
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <div className="flex flex-col lg:flex-row gap-16 lg:items-center">

          {/* Left — Image */}
          <motion.div {...fadeLeft(0.1)} className="w-full lg:w-[46%] flex-shrink-0">
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=900&q=80&auto=format&fit=crop"
                alt="Sustainable Civil Engineering"
                className="w-full h-[380px] lg:h-[460px] object-cover object-center"
              />
              {/* Orange overlay tint at bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-[#0d1117]/70 to-transparent" />

              {/* Floating badge */}
              <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between">
                <div className="bg-white rounded-xl px-4 py-3 shadow-lg">
                  <p className="text-gray-400 text-[10px] font-bold tracking-widest uppercase">Theme</p>
                  <p className="text-gray-900 text-[13px] font-black leading-snug mt-0.5">
                    Sustainable Materials<br />& Practices
                  </p>
                </div>
                <div className="bg-[#F58220] rounded-xl px-4 py-3 shadow-lg text-center">
                  <p className="text-white/80 text-[10px] font-bold tracking-widest uppercase">Edition</p>
                  <p className="text-white text-[22px] font-black leading-none mt-0.5">10th</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right — Content */}
          <div className="flex-1">
            {/* Section label */}
            <motion.div {...fadeUp(0.05)} className="flex items-center gap-3 mb-5">
              <div className="h-px w-8 bg-[#F58220]" />
              <span className="text-[#F58220] text-[11px] font-bold tracking-[0.22em] uppercase">
                About the Conference
              </span>
            </motion.div>

            <motion.h2
              {...fadeUp(0.12)}
              className="text-gray-900 text-3xl lg:text-[38px] font-black leading-tight tracking-tight mb-5"
            >
              Driving Innovation in
              <span className="text-[#F58220]"> Sustainable</span>
              <br />Civil Engineering
            </motion.h2>

            <motion.p {...fadeUp(0.18)} className="text-gray-500 text-[15px] leading-relaxed mb-4">
              SMPACE-2026 brings together scholars, engineers, and policy makers to discuss
              cutting-edge advancements that promote resilience, efficiency and durability in
              construction practices. The conference emphasises interdisciplinary collaboration,
              fostering the exchange of ideas and strategies for sustainable solutions.
            </motion.p>

            <motion.p {...fadeUp(0.22)} className="text-gray-500 text-[15px] leading-relaxed mb-8">
              Participants will explore ways to balance human development with ecological
              preservation, ensuring the longevity and functionality of structures through
              green construction practices, renewable materials and advanced engineering techniques.
            </motion.p>

            {/* Highlights checklist */}
            <motion.div {...fadeUp(0.28)} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {CONF_HIGHLIGHTS.map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 size={16} className="text-[#F58220] flex-shrink-0 mt-[2px]" />
                  <span className="text-gray-600 text-[13px] leading-snug">{item}</span>
                </div>
              ))}
            </motion.div>

            <motion.div {...fadeUp(0.34)}>
              <Link
                to="/research-areas"
                className="inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#d96d12] text-white text-[13px] font-bold px-6 py-3 rounded-lg transition-all duration-200 group"
              >
                Explore Research Areas
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════
          BLOCK 2 — About REVA University
      ════════════════════════════════════════ */}
      <div className="bg-gray-50 border-y border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="flex flex-col lg:flex-row gap-16 lg:items-center">

            {/* Left — Content */}
            <div className="flex-1">
              <motion.div {...fadeUp(0.05)} className="flex items-center gap-3 mb-5">
                <div className="h-px w-8 bg-[#F58220]" />
                <span className="text-[#F58220] text-[11px] font-bold tracking-[0.22em] uppercase">
                  About the Institution
                </span>
              </motion.div>

              <motion.h2
                {...fadeUp(0.12)}
                className="text-gray-900 text-3xl lg:text-[38px] font-black leading-tight tracking-tight mb-5"
              >
                REVA University
                <span className="block text-gray-400 text-xl lg:text-2xl font-semibold mt-1">
                  Bengaluru, Karnataka — Est. 2012
                </span>
              </motion.h2>

              <motion.p {...fadeUp(0.18)} className="text-gray-500 text-[15px] leading-relaxed mb-4">
                REVA University is a State Private University established under the Government of
                Karnataka Act No. 13 in the year 2012, located in Bengaluru — the IT capital of India.
                Recognised by the University Grants Commission (UGC) and approved by the All India
                Council for Technical Education (AICTE).
              </motion.p>

              <motion.p {...fadeUp(0.22)} className="text-gray-500 text-[15px] leading-relaxed mb-8">
                At the heart of REVA's philosophy is its guiding principle —&nbsp;
                <span className="text-gray-800 font-semibold">"Educate to Enterprise"</span>.
                This unique approach builds an entrepreneurial mindset across all disciplines,
                nurturing leaders, innovators and problem-solvers who think boldly and sustainably.
              </motion.p>

              {/* NAAC + Accreditation badges */}
              <motion.div {...fadeUp(0.27)} className="flex flex-wrap gap-3 mb-8">
                {[
                  { label: "NAAC Grade", value: "A+" },
                  { label: "UGC", value: "Recognised" },
                  { label: "AICTE", value: "Approved" },
                ].map((b, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm"
                  >
                    <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                      <CheckCircle2 size={15} className="text-[#F58220]" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-[10px] font-bold tracking-widest uppercase">
                        {b.label}
                      </p>
                      <p className="text-gray-900 text-[14px] font-black">{b.value}</p>
                    </div>
                  </div>
                ))}
              </motion.div>

              {/* Disciplines offered */}
              <motion.div {...fadeUp(0.32)}>
                <p className="text-gray-400 text-[11px] font-bold tracking-[0.18em] uppercase mb-3">
                  Disciplines Offered
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Engineering", "Commerce", "Law", "Management",
                    "Architecture", "Liberal Studies", "Performing Arts",
                  ].map((d) => (
                    <span
                      key={d}
                      className="bg-white border border-gray-200 text-gray-600 text-[12px] font-semibold px-3 py-1.5 rounded-full"
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Right — Stats card + image */}
            <motion.div {...fadeRight(0.15)} className="w-full lg:w-[44%] flex-shrink-0 space-y-5">

              {/* Stats grid */}
              <div className="grid grid-cols-2 gap-4">
                {REVA_STATS.map(({ value, unit, label }, i) => (
                  <div
                    key={i}
                    className="bg-white border border-gray-100 rounded-2xl px-6 py-5 shadow-sm text-center"
                  >
                    <p className="text-[#F58220] text-[36px] font-black leading-none">
                      {value}
                      <span className="text-[18px] ml-1">{unit}</span>
                    </p>
                    <p className="text-gray-400 text-[12px] font-semibold mt-1">{label}</p>
                  </div>
                ))}
              </div>

              {/* Campus image */}
              <div className="rounded-2xl overflow-hidden shadow-md">
                <img
                  src="https://images.unsplash.com/photo-1562774053-701939374585?w=800&q=80&auto=format&fit=crop"
                  alt="REVA University Campus"
                  className="w-full h-[220px] object-cover object-center"
                />
              </div>

            </motion.div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════
          BLOCK 3 — School of Civil Engineering
      ════════════════════════════════════════ */}
      <div className="bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="flex flex-col lg:flex-row gap-16 lg:items-start">

            {/* Left — Image */}
            <motion.div {...fadeLeft(0.1)} className="w-full lg:w-[44%] flex-shrink-0">
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1498243691581-b145c3f54a5a?w=900&q=80&auto=format&fit=crop"
                  alt="School of Civil Engineering"
                  className="w-full h-[400px] object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111827]/60 to-transparent" />
                <div className="absolute bottom-6 left-6">
                  <p className="text-white/70 text-[11px] font-bold tracking-widest uppercase">Organised by</p>
                  <p className="text-white text-[17px] font-black mt-1 leading-snug">
                    School of Civil Engineering<br />
                    <span className="text-orange-400 font-semibold text-[14px]">REVA University</span>
                  </p>
                </div>
              </div>

              {/* Organiser badge below image */}
              <div className="mt-5 bg-[#111827] rounded-2xl px-6 py-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#F58220]/20 flex items-center justify-center flex-shrink-0">
                  <Building2 size={18} className="text-[#F58220]" />
                </div>
                <div>
                  <p className="text-white text-[13px] font-black">Rukmini Educational Charitable Trust</p>
                  <p className="text-white/50 text-[12px] mt-0.5">Founding body of REVA University</p>
                </div>
              </div>
            </motion.div>

            {/* Right — Content */}
            <div className="flex-1">
              <motion.div {...fadeUp(0.05)} className="flex items-center gap-3 mb-5">
                <div className="h-px w-8 bg-[#F58220]" />
                <span className="text-[#F58220] text-[11px] font-bold tracking-[0.22em] uppercase">
                  Organising Department
                </span>
              </motion.div>

              <motion.h2
                {...fadeUp(0.12)}
                className="text-gray-900 text-3xl lg:text-[38px] font-black leading-tight tracking-tight mb-5"
              >
                School of
                <span className="text-[#F58220]"> Civil Engineering</span>
              </motion.h2>

              <motion.p {...fadeUp(0.18)} className="text-gray-500 text-[15px] leading-relaxed mb-4">
                The School of Civil Engineering is led by a highly experienced professor and
                supported by well-qualified faculty members. The school has state-of-the-art
                classrooms and well-equipped laboratories, delivering industry-oriented education
                that bridges the gap between academia and the civil engineering profession.
              </motion.p>

              <motion.p {...fadeUp(0.22)} className="text-gray-500 text-[15px] leading-relaxed mb-8">
                REVA University's Civil Engineering programme produces quality engineers who are
                effective and efficient in problem solving, providing economical and sustainable
                infrastructural solutions to society.
              </motion.p>

              {/* Programmes offered */}
              <motion.div {...fadeUp(0.28)}>
                <p className="text-gray-400 text-[11px] font-bold tracking-[0.18em] uppercase mb-4">
                  Programmes Offered
                </p>
                <div className="space-y-3">
                  {SCHOOL_PROGRAMS.map(({ icon: Icon, title, desc }, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.07, duration: 0.4 }}
                      className="flex items-center gap-4 bg-gray-50 border border-gray-100 rounded-xl px-5 py-3.5 hover:border-orange-200 hover:bg-orange-50/30 transition-all duration-200"
                    >
                      <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                        <Icon size={15} className="text-[#F58220]" />
                      </div>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="bg-[#F58220] text-white text-[10px] font-black tracking-wider px-2 py-0.5 rounded flex-shrink-0">
                          {title}
                        </span>
                        <span className="text-gray-700 text-[13px] font-medium truncate">{desc}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

            </div>
          </div>
        </div>
      </div>

    </section>
  );
}